import { useCallback, useEffect, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import { hardRefresh } from '../../../utils/hard-refresh';
import { markVersionPrompted } from '../../../utils/app-version';
import { clearEmployeeSessionStorage } from '../../../contexts/EmployeeSessionProvider';
import {
  REFRESH_CROSS_TAB_PULSE_KEY,
  REFRESH_BROADCAST_DONE_TOAST,
  REFRESH_DONE_TOAST,
  REFRESH_INITIATOR_KEY,
  REFRESH_INITIATOR_MAX_AGE_MS,
  REFRESH_MANAGER_DEFERRED_TOAST,
  REFRESH_MANAGER_NOW_TOAST,
  REFRESH_NOTICE_KEY,
  REFRESH_VERSION_DONE_TOAST,
  REFRESH_SESSION_CLOSED_DONE_TOAST,
  REFRESH_SESSION_OPENED_DONE_TOAST,
  REFRESH_SELF_DEFERRED_TOAST,
  REFRESH_VERSION_HEADS_UP_TOAST,
  type RefreshNotice,
} from './refresh.toasts';

// Reads (and optionally clears) the fresh initiator marker set by the broadcasting device.
function consumeInitiatorMarker(clear: boolean): boolean {
  try {
    const raw = sessionStorage.getItem(REFRESH_INITIATOR_KEY);
    if (!raw) {
      return false;
    }
    if (clear) {
      sessionStorage.removeItem(REFRESH_INITIATOR_KEY);
    }
    const age = Date.now() - Number(raw);
    return Number.isFinite(age) && age >= 0 && age <= REFRESH_INITIATOR_MAX_AGE_MS;
  } catch {
    return false;
  }
}

export type RefreshSource = 'self' | 'manager' | 'version' | 'session-opened' | 'session-closed';
export type RequestRefresh = (source: RefreshSource, version?: string) => void;

// A pending refresh fires only after the zone has been continuously safe for this long.
const SAFE_SETTLE_MS = 300;

// Delay before a forced version reload; 0 = instant, raise (~2000) for a heads-up toast first.
const VERSION_REFRESH_HEADS_UP_MS: number = 0;

function tenantIdFromCurrentPath(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const match = window.location.pathname.match(/^\/t\/([^/]+)\//);
  return match ? decodeURIComponent(match[1]) : null;
}

// Writes the post-reload notice, then hard-refreshes; the marker survives the reload that follows.
function applyRefresh(
  notice: RefreshNotice,
  source: RefreshSource,
  version: string | null,
  shouldEject: boolean,
): void {
  const tenantId = tenantIdFromCurrentPath();
  // Marked here, not at detection: a defer that never reloads must stay detectable next boot.
  if (source === 'version' && version) {
    markVersionPrompted(version);
  }
  // A closed session kicks every device back to the POS employee login on the reload that follows.
  if (shouldEject && tenantId) {
    clearEmployeeSessionStorage(tenantId);
  }
  // Session open/close reload gets its own post-reload notice, whatever the now/deferred path was.
  const stored: RefreshNotice =
    source === 'session-closed' || source === 'session-opened' ? source : notice;
  try {
    sessionStorage.setItem(REFRESH_NOTICE_KEY, stored);
  } catch {
    // sessionStorage may be unavailable (private mode); never block the reload on it.
  }
  try {
    localStorage.setItem(
      REFRESH_CROSS_TAB_PULSE_KEY,
      JSON.stringify({ at: Date.now(), tenantId }),
    );
  } catch {
    // localStorage may be unavailable; do not block refresh.
  }
  void hardRefresh();
}

// Architectural summary: gate; hold until safe, except a version mismatch which reloads at once.
export function useDeferredRefresh(isSafeZone: boolean): { requestRefresh: RequestRefresh } {
  const pendingRef = useRef<RefreshSource | null>(null);
  const pendingNoticeRef = useRef<RefreshNotice>('deferred');
  const latestVersionRef = useRef<string | null>(null);
  const versionFiredRef = useRef(false);
  // Sticky: a session-closed request must still eject even if a later source takes the slot.
  const shouldEjectRef = useRef(false);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSafeZoneRef = useRef(isSafeZone);
  isSafeZoneRef.current = isSafeZone;

  const clearSettle = useCallback(() => {
    if (settleTimerRef.current !== null) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  // Arm the settle countdown; fire the pending refresh after SAFE_SETTLE_MS of continuous safety.
  const armSettle = useCallback(() => {
    if (!isSafeZoneRef.current || pendingRef.current === null) {
      clearSettle();
      return;
    }
    if (settleTimerRef.current !== null) return;
    settleTimerRef.current = setTimeout(() => {
      settleTimerRef.current = null;
      // Re-verify safety at expiry: the zone may have flipped unsafe without a re-render.
      if (isSafeZoneRef.current && pendingRef.current !== null) {
        // hardRefresh reloads the page, so no need to clear pendingRef — the tab is replaced.
        applyRefresh(pendingNoticeRef.current, pendingRef.current, null, shouldEjectRef.current);
      }
    }, SAFE_SETTLE_MS);
  }, [clearSettle]);

  const requestRefresh = useCallback<RequestRefresh>((source, version) => {
    shouldEjectRef.current ||= source === 'session-closed';
    // Stale assets are a correctness risk (old JS vs new backend), so a new build never defers.
    if (source === 'version') {
      latestVersionRef.current = version ?? null;
      // Wake + re-open fire on one wake: one reload, and the newest build is the marked id.
      if (versionFiredRef.current) {
        if (version) markVersionPrompted(version);
        return;
      }
      versionFiredRef.current = true;
      const apply = () =>
        applyRefresh('version-now', 'version', latestVersionRef.current, shouldEjectRef.current);
      if (VERSION_REFRESH_HEADS_UP_MS > 0) {
        notifications.show(REFRESH_VERSION_HEADS_UP_TOAST);
        setTimeout(apply, VERSION_REFRESH_HEADS_UP_MS);
      } else {
        apply();
      }
      return;
    }
    // The broadcasting device stays silent pre-reload; its single combined toast shows post-reload.
    const isInitiator = source === 'manager' && consumeInitiatorMarker(false);
    // Same source already pending (wake + re-open both fire on one wake): no second toast.
    if (pendingRef.current === source) {
      armSettle();
      return;
    }
    // Last requester wins the pending slot; only the toast/notice differs, the eject stays sticky.
    pendingRef.current = source;
    if (isSafeZoneRef.current) {
      pendingNoticeRef.current = 'now';
      if (source === 'manager' && !isInitiator) {
        notifications.show(REFRESH_MANAGER_NOW_TOAST);
      }
    } else {
      pendingNoticeRef.current = 'deferred';
      if (!isInitiator) {
        notifications.show(
          source === 'manager' ? REFRESH_MANAGER_DEFERRED_TOAST : REFRESH_SELF_DEFERRED_TOAST,
        );
      }
    }
    armSettle();
  }, [armSettle]);

  // On any safe/unsafe flip: arm the countdown when safe, cancel it when the zone turns unsafe.
  useEffect(() => {
    if (isSafeZone) {
      armSettle();
    } else {
      clearSettle();
    }
  }, [isSafeZone, armSettle, clearSettle]);

  useEffect(() => clearSettle, [clearSettle]);

  // Shows the surviving post-reload notice once on mount, then clears it.
  useEffect(() => {
    let notice: RefreshNotice | null = null;
    try {
      notice = sessionStorage.getItem(REFRESH_NOTICE_KEY) as RefreshNotice | null;
      if (notice) {
        sessionStorage.removeItem(REFRESH_NOTICE_KEY);
      }
    } catch {
      // sessionStorage may be unavailable; skip the post-reload notice in that case.
    }
    if (!notice) {
      return;
    }
    if (notice === 'version-now') {
      notifications.show(REFRESH_VERSION_DONE_TOAST);
      return;
    }
    if (notice === 'session-closed') {
      notifications.show(REFRESH_SESSION_CLOSED_DONE_TOAST);
      return;
    }
    if (notice === 'session-opened') {
      notifications.show(REFRESH_SESSION_OPENED_DONE_TOAST);
      return;
    }
    notifications.show(consumeInitiatorMarker(true) ? REFRESH_BROADCAST_DONE_TOAST : REFRESH_DONE_TOAST);
  }, []);

  return { requestRefresh };
}
